import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import ArticleBody, { readingTime, fmtDate } from "@/components/ArticleBody";
import { ArticleCard } from "@/components/ArticlesSection";
import { ArrowLeft, Clock, CalendarCheck, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/brand";
import ShareButtons from "@/components/ShareButtons";

const useSeo = (a) => {
  useEffect(() => {
    if (!a) return undefined;
    const prevTitle = document.title;
    document.title = `${a.title} | PROAC Transmission`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    const prevDesc = meta.content;
    meta.content = a.meta_description || a.excerpt;
    const ld = document.createElement("script");
    const canon = document.createElement("link");
    canon.rel = "canonical"; canon.href = `${window.location.origin}/conseils/${a.slug}`;
    document.head.appendChild(canon);
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.meta_description || a.excerpt,
      image: a.image ? [a.image] : undefined, datePublished: a.published_at, dateModified: a.updated_at || a.published_at,
      author: { "@type": "Organization", name: "PROAC Transmission" },
      publisher: { "@type": "Organization", name: "PROAC Transmission" }, mainEntityOfPage: window.location.href,
    });
    document.head.appendChild(ld);
    return () => { document.title = prevTitle; meta.content = prevDesc; ld.remove(); canon.remove(); };
  }, [a]);
};

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [missing, setMissing] = useState(false);
  const [others, setOthers] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0); setA(null); setMissing(false);
    api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setMissing(true));
    api.get("/articles").then((r) => setOthers(r.data.filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);
  useSeo(a);

  if (missing) return (
    <div className="bg-[#FDFDFC] min-h-screen"><Header />
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="article-not-found">
        <h1 className="font-display text-3xl text-[#1B2A4A]">Article introuvable</h1>
        <Link to="/conseils" className="mt-6 inline-block text-[#C9A85A] underline">Retour aux conseils</Link>
      </div><Footer /></div>
  );

  return (
    <div className="bg-[#FDFDFC]">
      <Header />
      {!a ? <div className="py-40 text-center text-gray-400">Chargement…</div> : (
        <article data-testid="article-page">
          <header className="relative min-h-[420px] flex items-end">
            {a.image && <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D1629] via-[#0D1629]/75 to-[#0D1629]/30" />
            <div className="relative max-w-4xl mx-auto px-6 sm:px-8 w-full pb-14 pt-32">
              <Link to="/conseils#articles" data-testid="article-back" className="inline-flex items-center gap-1.5 text-white/70 hover:text-[#E0C173] text-sm"><ArrowLeft className="w-4 h-4" /> Conseils & articles</Link>
              <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-[#E0C173]">{a.category}{a.sector && ` · ${a.sector}`}</div>
              <h1 data-testid="article-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight">{a.title}</h1>
              <div className="mt-5 flex items-center gap-2 text-sm text-white/70"><Clock className="w-4 h-4" /> {fmtDate(a.published_at)} · {readingTime(a.content)} min de lecture</div>
            </div>
          </header>
          <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
            <p data-testid="article-excerpt" className="text-lg sm:text-xl text-[#1B2A4A] leading-relaxed border-l-4 border-[#E0C173] pl-5 mb-6">{a.excerpt}</p>
            <div className="mb-10"><ShareButtons slug={a.slug} title={a.title} testid="article-share-top" /></div>
            <ArticleBody content={a.content} />
            <div className="mt-12 pt-6 border-t border-gray-200"><ShareButtons slug={a.slug} title={a.title} testid="article-share" /></div>
            <aside data-testid="article-cta" className="mt-16 bg-[#1B2A4A] text-white p-8 sm:p-10">
              <h2 className="font-display text-2xl">Vous envisagez de céder votre entreprise ?</h2>
              <p className="mt-3 text-white/75">Premier échange gratuit, confidentiel et sans engagement avec un conseiller PROAC Transmission.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button data-testid="article-cta-rdv" onClick={() => navigate("/rdv-decouverte")} className="rounded-full bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold px-6"><CalendarCheck className="w-4 h-4 mr-2" /> RDV découverte gratuit</Button>
                <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-2 hover:border-[#E0C173] transition-colors"><Phone className="w-4 h-4" /> {PHONE_DISPLAY}</a>
              </div>
            </aside>
          </div>
          {others.length > 0 && (
            <section className="bg-[#F8F6F0] border-t border-[#E0C173]/30">
              <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20">
                <h2 className="font-display text-2xl sm:text-3xl text-[#1B2A4A]">À lire aussi</h2>
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{others.map((o, i) => <ArticleCard key={o.slug} a={o} i={i} />)}</div>
              </div>
            </section>
          )}
        </article>
      )}
      <Footer />
    </div>
  );
}
